import React from 'react';
import ContactForm from './ContactForm';
import Icon from './icons/Icon';
import {
  PHONE,
  PHONE_DISPLAY,
  WHATSAPP_LOCAL,
  CONTACT_EMAIL,
  INFO_EMAIL,
  ADDRESS,
  MAPS_EMBED_URL,
  MAPS_DIRECTIONS_URL,
  getWhatsAppLink,
} from '../config/site';
import { usePageContent } from '../hooks/usePageContent';
import { trackWhatsAppClick } from '../utils/analytics';
import './Contact.css';

const HEADER_DEFAULTS = {
  title: 'Get In Touch',
  description: "We're here to help you achieve your best smile. Reach out to schedule an appointment or ask any questions about our services.",
};

const INFO_DEFAULTS = {
  phone: PHONE_DISPLAY,
  email: INFO_EMAIL,
  address: ADDRESS.full,
  hours: "Monday - Friday: 8:00 AM - 8:00 PM\nSaturday: 8:00 AM - 6:00 PM\nSunday: Closed",
};

const Contact = () => {
  const { content: header } = usePageContent('contact', 'header', {
    title: 'Contact Us',
    description: HEADER_DEFAULTS.description,
  });
  const { content: info } = usePageContent('contact', 'info', INFO_DEFAULTS);

  const whatsappLink = getWhatsAppLink('Hello Dr. Muyabin, I would like to get in touch.');
  const displayPhone = info.phone || PHONE_DISPLAY;
  const displayEmail = info.email || INFO_EMAIL;
  const displayAddress = info.address || ADDRESS.full;
  const hoursLines = (info.hours || INFO_DEFAULTS.hours).split('\n').filter(Boolean);

  return (
    <section className="contact-section" id="contact">
      <div className="contact-container">
        <div className="contact-header">
          <span className="contact-subtitle">Contact Us</span>
          <h2 className="contact-title">{header.title || HEADER_DEFAULTS.title}</h2>
          <p className="contact-description">{header.description || HEADER_DEFAULTS.description}</p>
        </div>

        <div className="contact-content">
          <div className="contact-info">
            <div className="contact-item">
              <div className="contact-icon"><Icon name="mapPin" size={24} /></div>
              <div className="contact-details">
                <h4>Our Location</h4>
                <p>{displayAddress}</p>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon"><Icon name="phone" size={24} /></div>
              <div className="contact-details">
                <h4>Phone</h4>
                <p><a href={`tel:${displayPhone.replace(/\s/g, '')}`}>{displayPhone}</a></p>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon"><Icon name="mail" size={24} /></div>
              <div className="contact-details">
                <h4>Email</h4>
                <p><a href={`mailto:${displayEmail}`}>{displayEmail}</a></p>
                <p><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon"><Icon name="clock" size={24} /></div>
              <div className="contact-details">
                <h4>Office Hours</h4>
                {hoursLines.map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon"><Icon name="messageCircle" size={24} /></div>
              <div className="contact-details">
                <h4>Social Media & WhatsApp</h4>
                <div className="social-links">
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link whatsapp-link"
                    title="Chat on WhatsApp"
                    onClick={() => trackWhatsAppClick('contact_page')}
                  >
                    <span className="social-icon"><Icon name="messageCircle" size={18} /></span>
                    <span>WhatsApp: {WHATSAPP_LOCAL}</span>
                  </a>
                  <a
                    href="https://www.instagram.com/muya__dds"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link instagram-link"
                    title="Follow us on Instagram"
                  >
                    <span className="social-icon"><Icon name="instagram" size={18} /></span>
                    <span>Instagram</span>
                  </a>
                  <a
                    href="https://x.com/muya_bin"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link twitter-link"
                    title="Follow us on X (Twitter)"
                  >
                    <span className="social-icon"><Icon name="twitter" size={18} /></span>
                    <span>X (Twitter)</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-form-wrapper">
            <ContactForm />
          </div>
        </div>

        <div className="contact-map-section">
          <h3 className="map-title">Find Us</h3>
          <div className="contact-map">
            <iframe
              src={MAPS_EMBED_URL}
              width="100%"
              height="450"
              style={{ border: 0, borderRadius: '15px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Dr. Muyabin Dental Clinic Location"
            />
            <div className="map-info">
              <p>
                <strong>Address:</strong> {displayAddress}
              </p>
              <a
                href={MAPS_DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="map-directions-btn"
              >
                Get Directions →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
